import { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Chip, Avatar, Button, Stack, Divider, Paper, TextField
} from '@mui/material';
import { Close, Edit, Delete, Phone, WhatsApp, Mail, Add, Check } from '@mui/icons-material';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { STAGES, ACTIVITY_LABELS } from './constants';
import { fmtDateTime, fmtDate, nowISO } from './helpers';
import { useTags } from './useTags';
import ActivityModal from './ActivityModal';
import TaskModal from './TaskModal';

export default function ContactDetail({ contact, onClose, onEdit, onDelete }) {
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [modalAct, setModalAct] = useState(null);
  const [modalTask, setModalTask] = useState(null);
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState(contact.note || '');
  const { getColor } = useTags();

  // Aggiorna nota locale se il contatto cambia da fuori
  useEffect(() => {
    if (!editingNote) setNoteValue(contact.note || '');
  }, [contact.note, editingNote]);

  useEffect(() => {
    const u1 = onSnapshot(
      query(collection(db, 'crm_attivita'), where('contattoId', '==', contact.id), orderBy('data', 'desc')),
      s => setActivities(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const u2 = onSnapshot(
      query(collection(db, 'crm_task'), where('contattoId', '==', contact.id)),
      s => setTasks(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { u1(); u2(); };
  }, [contact.id]);

  const updateStage = async (stage) => {
    await updateDoc(doc(db, 'crm_contatti', contact.id), { stage, updatedAt: nowISO() });
  };

  const saveNote = async () => {
    await updateDoc(doc(db, 'crm_contatti', contact.id), { note: noteValue, updatedAt: nowISO() });
    setEditingNote(false);
  };

  const toggleTask = async (t) => {
    await updateDoc(doc(db, 'crm_task', t.id), { fatto: !t.fatto });
  };

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      {/* Header */}
      <Box sx={{ p: 2, pt: { xs: 10, sm: 10 }, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>{(contact.nome || '?').charAt(0).toUpperCase()}</Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{contact.nome}</Typography>
          {contact.azienda && <Typography variant="caption" color="text.secondary">{contact.azienda}</Typography>}
        </Box>
        <IconButton onClick={onEdit}><Edit fontSize="small" /></IconButton>
        <IconButton onClick={onDelete}><Delete fontSize="small" /></IconButton>
        <IconButton onClick={onClose}><Close /></IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {/* Azioni rapide */}
        <Stack direction="row" useFlexGap sx={{ mb: 2, flexWrap: 'wrap', columnGap: 1, rowGap: 1 }}>
          {contact.telefono && <Button size="small" startIcon={<Phone />} href={'tel:' + contact.telefono} variant="outlined">Chiama</Button>}
          {contact.telefono && <Button size="small" startIcon={<WhatsApp />} href={'https://wa.me/' + contact.telefono.replace(/[^0-9]/g, '')} target="_blank" variant="outlined">WhatsApp</Button>}
          {contact.email && <Button size="small" startIcon={<Mail />} href={'mailto:' + contact.email} variant="outlined">Email</Button>}
          {contact.googleMapsLink && <Button size="small" href={contact.googleMapsLink} target="_blank" variant="outlined">Maps</Button>}
          {contact.sitoWeb && <Button size="small" href={contact.sitoWeb.startsWith('http') ? contact.sitoWeb : 'https://' + contact.sitoWeb} target="_blank" variant="outlined">Sito Web</Button>}
        </Stack>

        {/* Info */}
        {(contact.indirizzo || contact.provincia || contact.valutazione) && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
            {contact.indirizzo && <Typography variant="body2"><strong>Indirizzo:</strong> {contact.indirizzo}</Typography>}
            {contact.provincia && <Typography variant="body2"><strong>Provincia:</strong> {contact.provincia}</Typography>}
            {contact.valutazione && <Typography variant="body2"><strong>Valutazione Google:</strong> {contact.valutazione}/5</Typography>}
          </Paper>
        )}

        {/* Tag */}
        {contact.tag && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Tag</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={contact.tag} size="small" sx={{ bgcolor: getColor(contact.tag), color: 'white', fontWeight: 600 }} />
            </Box>
          </Box>
        )}

        {/* Stage */}
        <Typography variant="caption" color="text.secondary">Stage pipeline</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, mt: 0.5 }}>
          {STAGES.map(s => (
            <Chip key={s.id} label={s.label} size="small" clickable onClick={() => updateStage(s.id)}
              sx={{ bgcolor: contact.stage === s.id ? s.color : 'transparent', color: contact.stage === s.id ? 'white' : s.color, border: 1, borderColor: s.color, fontWeight: contact.stage === s.id ? 700 : 400 }} />
          ))}
        </Box>

        {/* Note — editabili inline */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Note</Typography>
            {!editingNote ? (
              <IconButton size="small" onClick={() => setEditingNote(true)}>
                <Edit sx={{ fontSize: 14 }} />
              </IconButton>
            ) : (
              <IconButton size="small" color="primary" onClick={saveNote}>
                <Check sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>

          {editingNote ? (
            <TextField
              multiline
              fullWidth
              minRows={3}
              size="small"
              value={noteValue}
              onChange={e => setNoteValue(e.target.value)}
              autoFocus
              placeholder="Scrivi qui le annotazioni su questo contatto..."
              variant="outlined"
              sx={{ bgcolor: 'white' }}
              onKeyDown={e => {
                if (e.key === 'Escape') { setEditingNote(false); setNoteValue(contact.note || ''); }
              }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', cursor: 'text', minHeight: 40, color: noteValue ? 'text.primary' : 'text.disabled' }}
              onClick={() => setEditingNote(true)}
            >
              {noteValue || 'Clicca per aggiungere una nota...'}
            </Typography>
          )}
        </Paper>

        {/* Attività */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Attività ({activities.length})</Typography>
          <Button size="small" startIcon={<Add />} onClick={() => setModalAct({ contattoId: contact.id, tipo: 'call', data: new Date().toISOString().slice(0, 16) })}>Registra</Button>
        </Box>
        <Stack spacing={1} sx={{ mb: 2 }}>
          {activities.length === 0 ? <Typography variant="caption" color="text.secondary">Nessuna attività.</Typography> : activities.map(a => (
            <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {ACTIVITY_LABELS[a.tipo]}{a.esito && ' · ' + a.esito}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDateTime(a.data)} · {a.owner}</Typography>
                  {a.note && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{a.note}</Typography>}
                  {a.prossimoStep && <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>→ {a.prossimoStep}</Typography>}
                </Box>
                <IconButton size="small" onClick={() => setModalAct(a)}><Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => window.confirm('Eliminare?') && deleteDoc(doc(db, 'crm_attivita', a.id))}><Delete fontSize="small" /></IconButton>
              </Box>
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Task */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Task ({tasks.length})</Typography>
          <Button size="small" startIcon={<Add />} onClick={() => setModalTask({ contattoId: contact.id, scadenza: new Date().toISOString().slice(0, 10), priority: 'media' })}>Nuovo</Button>
        </Box>
        <Stack spacing={0.5}>
          {tasks.length === 0 ? <Typography variant="caption" color="text.secondary">Nessun task.</Typography> : tasks.map(t => (
            <Box key={t.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <input type="checkbox" checked={!!t.fatto} onChange={() => toggleTask(t)} style={{ marginTop: 4 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ textDecoration: t.fatto ? 'line-through' : 'none', color: t.fatto ? 'text.disabled' : 'text.primary' }}>{t.titolo}</Typography>
                <Typography variant="caption" color="text.secondary">{t.scadenza ? fmtDate(t.scadenza) : 'Senza data'} · {t.owner}</Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {modalAct && <ActivityModal initial={modalAct} onClose={() => setModalAct(null)} />}
      {modalTask && <TaskModal initial={modalTask} onClose={() => setModalTask(null)} />}
    </Drawer>
  );
}